import React, { FC, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import {
  forgotPassword,
  resetForgotPasswordState,
  selectForgotPassword,
} from "store/slices/authSlice";
import { Formik, Form, Field, ErrorMessage } from "formik";
import AuthLayout from "components/AuthLayout/AuthLayout";
import { motion } from "framer-motion";
import { authPageMeta } from "data/authPages.enum";
import { IForgotPassword } from "Interfaces/interface.auth";
import { useAppDispatch } from "store/hooks/hooks";
import { EMAIL_PATTERN } from "utils/validationRegex";

const inputClasses = `block w-full border-neutral-200 focus:border-primary-300 focus:ring focus:ring-primary-200 
  focus:ring-opacity-50 bg-white dark:border-neutral-700 dark:focus:ring-primary-6000 dark:focus:ring-opacity-25 
  dark:bg-neutral-900 disabled:bg-neutral-200 dark:disabled:bg-neutral-800 rounded-2xl text-sm font-normal h-11 px-4 py-3`;

const validateForm = (values: IForgotPassword) => {
  const errors: Partial<IForgotPassword> = {};
  if (!values.email) {
    errors.email = "Email is required";
  } else if (!EMAIL_PATTERN.test(values.email)) {
    errors.email = "Please enter a valid email address";
  }
  return errors;
};

const AccountForgotPass: FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { loading, success } = useSelector(selectForgotPassword);

  const formInitValues: IForgotPassword = { email: "" };

  const handleSubmit = async (values: IForgotPassword) => {
    try {
      await dispatch(forgotPassword({ email: values.email })).unwrap();
      navigate(`/reset-password?email=${encodeURIComponent(values.email)}`);
    } catch (err: any) {
      console.log("err: ", err);
    }
  };

  useEffect(() => {
    return () => {
      dispatch(resetForgotPasswordState());
    };
  }, [dispatch]);

  const handleNavigate = () => {
    navigate(-1);
  };

  return (
    <>
      <Helmet>
        <title>Reset Password || 360CUSTOMIZER</title>
      </Helmet>

      <AuthLayout {...authPageMeta.forgot}>
        <motion.div
          className="w-full flex justify-center items-center"
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" as const, delay: 0.2 }}
        >
          <div className="bg-white dark:bg-neutral-800 rounded-2xl w-full max-w-md p-6">
            <h2 className="text-2xl font-semibold text-neutral-900 dark:text-neutral-100 mb-4 text-center font-display">
              Forgot Password
            </h2>
            <Formik
              initialValues={formInitValues}
              validate={validateForm}
              onSubmit={handleSubmit}
            >
              {({ isSubmitting }) => (
                <Form className="grid grid-cols-1 gap-6">
                  <label className="block">
                    <span className="text-neutral-800 dark:text-neutral-200">
                      Email address
                    </span>
                    <Field
                      id="email"
                      name="email"
                      type="email"
                      placeholder="name@company.com"
                      className={`mt-1 disabled:cursor-not-allowed disabled:opacity-50 ${inputClasses}`}
                      disabled={loading || isSubmitting}
                    />
                    <ErrorMessage
                      name="email"
                      component="div"
                      className="text-red-500 text-sm mt-1"
                    />
                  </label>
                  <motion.div
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.97 }}
                  >
                    <ButtonPrimary
                      type="submit"
                      disabled={loading || isSubmitting}
                      loading={loading || isSubmitting}
                      sizeClass="w-full"
                    >
                      {!loading && !isSubmitting && "Send Verification Code"}
                    </ButtonPrimary>
                  </motion.div>
                </Form>
              )}
            </Formik>

            {success && (
              <p className="text-green-500 text-sm text-center mt-4">
                Verification code sent! Check your email.
              </p>
            )}

            <p className="block text-center mt-6 font-body text-sm">
              <button
                className="text-primary-500 hover:text-primary-600 font-medium hover:underline transition-colors duration-300 disabled:pointer-events-none disabled:opacity-50"
                onClick={handleNavigate}
                disabled={loading}
              >
                Go Back
              </button>
            </p>
          </div>
        </motion.div>
      </AuthLayout>
    </>
  );
};

export default AccountForgotPass;
